import {Typography, Card, Button, Space, Alert} from 'antd';
import {CrownOutlined} from '@ant-design/icons';
import {Link, useNavigate} from 'react-router-dom';
import {useContext} from 'react';
import {AuthContext} from '../../context/AuthContext';
import {PageTitle} from "../../components/PageTitle.tsx";
import {useSubscriptionEndDate} from '../../hooks/useSubscriptionEndDate';

const {Text, Paragraph} = Typography;

export const SilverPlusMembership = () => {
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const { endDate, formattedDate, accessEndDate, formattedAccessEndDate, subscriptionDetails } = useSubscriptionEndDate();

    // Check if subscription is active (end date is valid and in the future)
    const hasActiveSubscription = endDate && !isNaN(endDate.getTime()) && endDate > new Date();

    // Format price from cents to euros
    const formatPrice = (amount: number) => {
        const price = (amount / 100).toFixed(2);
        return `${price}€`;
    };

    // Determine subscription status and behavior based on subscription details from hook
    const isSubscriptionActive = hasActiveSubscription;
    const isCanceled = subscriptionDetails?.status === 'canceled';
    const hasCanceledAt = !!subscriptionDetails?.canceled_at;
    const hasCancelAt = !!subscriptionDetails?.cancel_at;

    // Determine the specific cancellation state
    const isCanceledButStillActive = hasCancelAt && isSubscriptionActive;
    const isScheduledForCancellation = hasCancelAt && !hasCanceledAt;

    // Get appropriate date and message based on subscription state
    const getPaymentInfo = () => {
        if (!hasActiveSubscription || !formattedDate) {
            return {
                date: '',
                message: 'Kein aktives Abonnement',
                type: 'info',
                showPaymentAmount: false
            };
        }

        if (isCanceled) {
            return {
                date: formattedAccessEndDate || formattedDate,
                message: `Ihr Abonnement wurde gekündigt und endete am`,
                type: 'error',
                showPaymentAmount: false
            };
        } else if (isCanceledButStillActive) {
            // Only show next payment if still in commitment period (accessEndDate > nextPaymentDate)
            const hasRemainingPayments = accessEndDate && endDate && accessEndDate > endDate;
            return {
                date: formattedAccessEndDate || formattedDate,
                nextPaymentDate: formattedDate,
                message: `Sie haben Ihr Abonnement gekündigt. silver+ Features enden am`,
                type: 'warning',
                showPaymentAmount: false,
                showNextPayment: hasRemainingPayments
            };
        } else if (isScheduledForCancellation) {
            // Only show next payment if still in commitment period (accessEndDate > nextPaymentDate)
            const hasRemainingPayments = accessEndDate && endDate && accessEndDate > endDate;
            return {
                date: formattedAccessEndDate || formattedDate,
                nextPaymentDate: formattedDate,
                message: `Ihr Abonnement ist zur Kündigung vorgemerkt und endet am`,
                type: 'warning',
                showPaymentAmount: false,
                showNextPayment: hasRemainingPayments
            };
        } else {
            return {
                date: formattedDate,
                message: `Die nächste Zahlung von`,
                type: 'info',
                showPaymentAmount: true
            };
        }
    };

    // Get plan name from user context
    const getPlanName = () => {
        return user?.plan_label || 'silver+ Abonnement';
    };

    const paymentInfo = getPaymentInfo();

    const subscriptionData = {
        isActive: isSubscriptionActive,
        plan: getPlanName(),
        price: subscriptionDetails ? formatPrice(subscriptionDetails.price?.amount || 0) : '0€',
        paymentInfo,
        status: subscriptionDetails?.status || 'unknown',
        isCanceled,
        isCanceledButStillActive,
        isScheduledForCancellation
    };

    const handleCancelMembership = () => {
        navigate('/account/cancel-silverplus');
    };

    return (
        <div>
            <PageTitle title="Meine silver+ Mitgliedschaft"/>

                <Card
                    style={{marginBottom: '24px'}}
                >
                    {subscriptionData.isActive ? (
                    <div>
                        <Paragraph style={{fontSize: '16px', marginBottom: '16px'}}>
                            {subscriptionData.paymentInfo.showPaymentAmount ? (
                                <>
                                    {subscriptionData.paymentInfo.message} <Text strong>{subscriptionData.price}</Text> ist fällig am <Text strong>{subscriptionData.paymentInfo.date}</Text>
                                </>
                            ) : (
                                <>
                                    {subscriptionData.paymentInfo.message} <Text strong>{subscriptionData.paymentInfo.date}</Text>
                                    {subscriptionData.paymentInfo.showNextPayment && subscriptionData.paymentInfo.nextPaymentDate && (
                                        <>. Die nächste Zahlung von <Text strong>{subscriptionData.price}</Text> ist fällig am <Text strong>{subscriptionData.paymentInfo.nextPaymentDate}</Text></>
                                    )}
                                </>
                            )}
                        </Paragraph>

                        <Alert
                            message={
                                subscriptionData.isCanceled
                                    ? `Abgelaufener Plan: ${subscriptionData.plan}`
                                    : subscriptionData.isCanceledButStillActive
                                        ? `Gekündigter Plan: ${subscriptionData.plan} (${subscriptionData.price}/Monat)`
                                        : subscriptionData.isScheduledForCancellation
                                            ? `Aktueller Plan: ${subscriptionData.plan} (${subscriptionData.price}/Monat)`
                                            : `Gewählter Abonnementplan: ${subscriptionData.plan} (${subscriptionData.price}/Monat)`
                            }
                            type={subscriptionData.paymentInfo.type as 'info' | 'warning' | 'error'}
                            showIcon
                            style={{marginBottom: '16px'}}
                        />

                        {!subscriptionData.isCanceled && !subscriptionData.isCanceledButStillActive && !subscriptionData.isScheduledForCancellation && (
                            <Paragraph style={{color: '#666', marginBottom: '24px'}}>
                                Nach der Mindestlaufzeit verlängert sich das Abonnement automatisch auf unbestimmte Zeit zum monatlichen Tarif und kann jederzeit mit einer Frist von einem Monat gekündigt werden.
                            </Paragraph>
                        )}

                        {(subscriptionData.isCanceledButStillActive || subscriptionData.isScheduledForCancellation) && (
                            <Paragraph style={{color: '#666', marginBottom: '24px'}}>
                                Sie haben weiterhin Zugang zu allen silver+ Features bis zum Ende Ihrer aktuellen Abrechnungsperiode. Danach wird Ihr Konto automatisch auf den kostenlosen Plan herabgestuft.
                            </Paragraph>
                        )}

                        <Space direction="vertical" style={{width: '100%'}}>
                            {!subscriptionData.isCanceled && !subscriptionData.isCanceledButStillActive && !subscriptionData.isScheduledForCancellation && (
                                <Button
                                    danger
                                    onClick={handleCancelMembership}
                                    style={{width: 'fit-content'}}
                                >
                                    silver+ Mitgliedschaft kündigen
                                </Button>
                            )}

                            <Paragraph style={{color: '#666', fontSize: '14px', marginTop: '16px'}}>
                                <Text strong>Etwas nicht in Ordnung?</Text> Sie möchten silver+ weiterhin nutzen, aber etwas funktioniert nicht?{' '}
                                <Link to="/contact" style={{color: '#00a9b2'}}>Kontaktieren Sie uns</Link>
                            </Paragraph>
                        </Space>
                    </div>
                ) : (
                        <Button
                            type="primary"
                            icon={<CrownOutlined />}
                            onClick={() => window.location.href = '/silverplus'}
                        >
                            silver+ freischalten
                        </Button>
                )}
                </Card>
        </div>
    );
};
